import { useState } from 'react';
import type { OfferPreviewItem } from '../../types/offers';
import type { OffersT } from './offersI18n';
import { formatAed } from './offerUtils';

/** Rows shown before "Show all": car-specific services can have 38+ variants. */
export const VARIANT_ROWS_COLLAPSED = 6;

/** Floored percent for display: "<1" when there is a saving the floor rounds to 0 (API keeps the int). */
export function percentText(percentOff: number, saving: number): string {
  return percentOff <= 0 && saving > 0 ? '<1' : String(percentOff);
}

interface Props {
  item: OfferPreviewItem;
  t: OffersT;
  /** Start expanded (default true). */
  defaultOpen?: boolean;
}

/**
 * Decision "4 preview" — one service's prices under the offer rule, per bookable variant:
 * Before → After (saving, %). Every figure comes from the server; a variant the rule leaves at its
 * list price gets a warning row. The admin portal mirrors this component (OfferVariantPrices).
 */
export default function VariantPriceTable({ item, t, defaultOpen = true }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const rows = item.variants;
  const visible = showAll ? rows : rows.slice(0, VARIANT_ROWS_COLLAPSED);
  const hidden = rows.length - visible.length;

  const maxSaving = rows.reduce((m, r) => Math.max(m, r.discounted ? r.saving : 0), 0);
  const pct = percentText(item.maxPercentOff, maxSaving);
  const summary = rows.length === 1
    ? t('offers.variants.summaryOne', { pct })
    : t('offers.variants.summary', { n: rows.length, pct });

  return (
    <div className="vpt" data-testid={`variant-prices-${item.offeringId}`}>
      <button type="button" className="vpt__toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="vpt__chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="vpt__name">{item.name}</span>
        <span className="vpt__summary">{rows.length ? summary : t('offers.variants.noPrices')}</span>
        {item.skippedCount > 0 && (
          <span className="vpt__warn-chip">{t('offers.variants.skippedSummary', { n: item.skippedCount })}</span>
        )}
      </button>
      {!item.bookable && <div className="vpt__note vpt__note--warn">{t('offers.variants.notBookable')}</div>}
      {open && rows.length > 0 && (
        <>
          <table className="vpt__table">
            <thead>
              <tr>
                <th scope="col">{t('offers.variants.col.variant')}</th>
                <th scope="col">{t('offers.variants.col.before')}</th>
                <th scope="col">{t('offers.variants.col.after')}</th>
                <th scope="col">{t('offers.variants.col.saving')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={r.variantId ?? `flat-${i}`} className={r.discounted ? undefined : 'vpt__row--skipped'}
                  data-testid={r.discounted ? 'variant-row' : 'variant-row-skipped'}>
                  <td>{r.label ?? t('offers.variants.standard')}</td>
                  <td dir="ltr">{r.discounted ? <s>{formatAed(r.listPrice)}</s> : formatAed(r.listPrice)}</td>
                  <td dir="ltr">{r.discounted ? <b>{formatAed(r.offerPrice)}</b> : formatAed(r.offerPrice)}</td>
                  <td>
                    {r.discounted
                      ? <span dir="ltr">{formatAed(r.saving)} ({percentText(r.percentOff, r.saving)}%)</span>
                      : <span className="vpt__warn" role="note">⚠ {t(`offers.variants.skip.${r.skipReason ?? 'NO_DISCOUNT'}`)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > VARIANT_ROWS_COLLAPSED && (
            <button type="button" className="vpt__more" onClick={() => setShowAll((s) => !s)}>
              {showAll ? t('offers.variants.showFewer') : t('offers.variants.showAll', { n: rows.length, hidden })}
            </button>
          )}
        </>
      )}
    </div>
  );
}
